/* Countdown dates without an explicit offset are merchant-entered Sydney time. */
if (!customElements.get('pd-countdown')) {
  customElements.define('pd-countdown', class extends HTMLElement {
    static parseDate(value) {
      const input = value.trim();
      if (/T.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(input)) return Date.parse(input);
      const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(input);
      if (!match) return NaN;
      const [, year, month, day, hour, minute, second = '0'] = match;
      const wallTime = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
      const date = new Date(wallTime);
      if (date.getUTCFullYear() !== +year || date.getUTCMonth() !== +month - 1 ||
          date.getUTCDate() !== +day || +hour > 23 || +minute > 59 || +second > 59) return NaN;
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Australia/Sydney', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      const toWallTime = (timestamp) => {
        const parts = Object.fromEntries(formatter.formatToParts(timestamp).map(({ type, value }) => [type, value]));
        return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
      };
      // Sample both sides of a DST transition. Reject nonexistent spring times;
      // for the repeated autumn hour choose the earlier occurrence consistently.
      const candidates = [-86400000, 0, 86400000].map((delta) => {
        const sample = wallTime + delta;
        return wallTime - (toWallTime(sample) - sample);
      }).filter((candidate) => toWallTime(candidate) === wallTime);
      return candidates.length ? Math.min(...candidates) : NaN;
    }
    static syncOffsets() {
      let height = 0;
      let stickyHeight = 0;
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      document.querySelectorAll('pd-countdown').forEach((timer) => {
        if (timer.hidden) return;
        const currentHeight = timer.offsetHeight;
        height += currentHeight;
        if (timer.dataset[desktop ? 'stickyDesktop' : 'stickyMobile'] === 'true') stickyHeight += currentHeight;
      });
      document.documentElement.style.setProperty('--countdown-height', `${height}px`);
      document.documentElement.style.setProperty('--header-sticky-top', `${stickyHeight}px`);
    }
    connectedCallback() {
      this.targetTime = this.constructor.parseDate(this.dataset.countdownEnd || '');
      this.ended = false;
      this.digits = {};
      ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => {
        this.digits[unit] = ['tens', 'ones'].map((digit) =>
          this.querySelector(`[data-time-unit="${unit}"][data-digit="${digit}"]`));
      });
      if (!Number.isFinite(this.targetTime)) {
        this.hidden = true;
        this.constructor.syncOffsets();
        return;
      }
      this.hidden = false;
      this.update();
      if (!this.ended) this.interval = setInterval(() => this.update(), 1000);
      this.observer = new ResizeObserver(() => this.constructor.syncOffsets());
      this.observer.observe(this);
      this.breakpoint = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = () => this.constructor.syncOffsets();
      this.breakpoint.addEventListener('change', this.onBreakpoint);
      this.constructor.syncOffsets();
    }
    update() {
      const remaining = Math.max(0, Math.ceil((this.targetTime - Date.now()) / 1000));
      const values = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor(remaining / 3600) % 24,
        minutes: Math.floor(remaining / 60) % 60,
        seconds: remaining % 60
      };
      Object.entries(values).forEach(([unit, value]) => {
        [Math.floor(value / 10), value % 10].forEach((number, index) => {
          const digit = this.digits[unit][index];
          if (digit && digit.textContent !== String(number)) digit.textContent = String(number);
        });
      });
      if (remaining || this.ended) return;
      this.ended = true;
      clearInterval(this.interval);
      this.hidden = this.dataset.autoHide === 'true';
      this.constructor.syncOffsets();
      this.dispatchEvent(new CustomEvent('countdownEnded', { detail: { countdown: this } }));
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      this.observer?.disconnect();
      this.breakpoint?.removeEventListener('change', this.onBreakpoint);
      this.constructor.syncOffsets();
    }
  });
}
